/**
 * ResidentComplianceHealthTile — Iter-99 · CEO Board read-only tile.
 *
 * Renders the company-wide Silver / Gold / Platinum distribution
 * fetched from GET /api/rewards/distribution. Strictly read-only
 * per the permanent CEO hierarchy rule.
 */
import { useEffect, useState } from 'react';
import { apiSafeFetch } from '../../lib/apiFetch';
import { Card } from '../ui/card';
import { Award, Users } from 'lucide-react';
import { useAdminScopeTerms } from '../../lib/adminScopeTerms';
import { useAdminScopeVersion } from '../../lib/adminScopeTransport';

const TIER_META = [
  { key: 'silver',   label: 'Silver',   color: '#8B8B8B', bg: 'rgba(139,139,139,0.12)' },
  { key: 'gold',     label: 'Gold',     color: '#C9A962', bg: 'rgba(201,169,98,0.12)' },
  { key: 'platinum', label: 'Platinum', color: '#7048E8', bg: 'rgba(112,72,232,0.12)' },
];

export default function ResidentComplianceHealthTile() {
  const terms = useAdminScopeTerms();
  const scopeVersion = useAdminScopeVersion();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiSafeFetch('/api/rewards/distribution').then(({ data: d }) => {
      if (!alive) return;
      setData(d);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [scopeVersion]);

  if (loading) return null;
  if (!data) return null;

  const total = data.total_residents || 0;
  const counts = data.counts || {};
  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);

  return (
    <section data-testid="ceo-resident-compliance-tile">
      <h2 className="text-xs uppercase tracking-widest text-[#FFD60A] mb-3 font-semibold">
        {terms.occupant} Compliance Health
      </h2>
      <Card className="p-5 bg-zinc-900 border-zinc-800 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-[#FFD60A]" />
            <div>
              <p className="text-sm font-bold">Company-Wide Reward Distribution</p>
              <p className="text-[11px] text-zinc-500">
                Silver · Gold · Platinum — computed by Operations, read-only.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-zinc-400">
            <Users className="h-4 w-4" />
            <span data-testid="ceo-resident-total">{total} {terms.occupants}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {TIER_META.map((t) => {
            const count = counts[t.key] || 0;
            const share = pct(count);
            return (
              <div
                key={t.key}
                className="rounded-lg p-3"
                style={{ backgroundColor: t.bg, border: `1px solid ${t.color}` }}
                data-testid={`ceo-reward-tile-${t.key}`}
              >
                <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: t.color }}>
                  {t.label}
                </p>
                <p className="text-2xl font-black text-white leading-tight">{count}</p>
                <p className="text-[11px] text-zinc-400">{share}% of {terms.occupants}</p>
              </div>
            );
          })}
          <div
            className="rounded-lg p-3"
            style={{ backgroundColor: 'rgba(63,63,70,0.35)', border: '1px solid #52525b' }}
            data-testid="ceo-reward-tile-none"
          >
            <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">
              Not Yet Eligible
            </p>
            <p className="text-2xl font-black text-white leading-tight">{counts.none || 0}</p>
            <p className="text-[11px] text-zinc-400">{pct(counts.none || 0)}% of {terms.occupants}</p>
          </div>
        </div>
      </Card>
    </section>
  );
}
